import React, { useState } from 'react';
import { UserProfile } from '../../../types/user';
import styles from '../AdminPanel.module.css';
import { MdSearch, MdFilterList, MdClose } from 'react-icons/md';

interface UserManagementProps {
  users: UserProfile[];
  onMakeAdmin: (userId: string) => Promise<void>;
  onMakePremium: (userId: string) => Promise<void>;
  onRemovePremium: (userId: string) => Promise<void>;
  onSuspendUser: (userId: string, reason: string) => Promise<void>;
  onUnsuspendUser: (userId: string) => Promise<void>;
  onDeleteUser: (userId: string) => Promise<void>;
  onSearch: () => Promise<void>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    hasMore: boolean;
  };
  loadNextPage: () => Promise<void>;
}

const UserManagement: React.FC<UserManagementProps> = ({ 
  users, 
  onMakeAdmin, 
  onMakePremium, 
  onRemovePremium,
  onSuspendUser, 
  onUnsuspendUser, 
  onDeleteUser,
  onSearch,
  searchTerm,
  setSearchTerm,
  pagination,
  loadNextPage
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'premium' | 'suspended'>('all');
  const [suspensionReason, setSuspensionReason] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleSuspendUser = () => {
    if (selectedUser && suspensionReason) {
      onSuspendUser(selectedUser.uid, suspensionReason);
      setShowSuspendModal(false);
      setSuspensionReason('');
      setSelectedUser(null);
    }
  };
  
  const openSuspendModal = (user: UserProfile) => {
    setSelectedUser(user);
    setShowSuspendModal(true);
  };
  
  // Filtreleme fonksiyonu
  const filteredUsers = users.filter(user => {
    if (roleFilter !== 'all') {
      if (roleFilter === 'admin' && user.role !== 'admin') return false;
      if (roleFilter === 'user' && user.role === 'admin') return false;
    }
    
    if (statusFilter !== 'all') {
      if (statusFilter === 'premium' && !user.isPremium) return false;
      if (statusFilter === 'suspended' && !user.suspended) return false;
    }
    
    return true;
  });
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <div>
      <h2>Kullanıcı Yönetimi</h2>
      
      <div className={styles.searchContainer}>
        <input 
          type="text" 
          placeholder="Kullanıcı adı veya e-posta ile ara..." 
          value={searchTerm}
          onChange={handleSearchInputChange}
          onKeyDown={handleKeyDown}
          className={styles.searchInput}
        />
        <button 
          className={styles.searchButton} 
          onClick={onSearch}
        >
          <MdSearch /> Ara
        </button>
        
        <button 
          className={styles.filterButton} 
          onClick={() => setShowFilters(!showFilters)}
        >
          <MdFilterList /> Filtrele
        </button>
      </div>
      
      {showFilters && (
        <div className={styles.filtersContainer}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Rol:</label>
            <select 
              className={styles.filterSelect}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
            >
              <option value="all">Tümü</option>
              <option value="admin">Yönetici</option>
              <option value="user">Kullanıcı</option>
            </select>
          </div>
          
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Durum:</label>
            <select 
              className={styles.filterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="all">Tümü</option>
              <option value="premium">Premium</option>
              <option value="suspended">Askıya Alınmış</option>
            </select>
          </div>
        </div>
      )}
      
      <div className={styles.cardSection}>
        <div className={styles.tableContainer}>
          <table className={styles.usersTable}>
            <thead>
              <tr>
                <th>Kullanıcı</th>
                <th>E-posta</th>
                <th>Rol</th>
                <th>Premium</th>
                <th>Durum</th>
                <th>Kayıt Tarihi</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <tr key={user.uid}>
                    <td className={styles.userCell}>
                      <div className={styles.userInfo}>
                        <div className={styles.userAvatar}>
                          {user.photoURL ? (
                            <img 
                              src={user.photoURL} 
                              alt={user.displayName || ''} // null yerine boş string kullan
                            />
                          ) : (
                            <div className={styles.defaultAvatar}>
                              {user.displayName?.charAt(0) || 'U'}
                            </div>
                          )}
                        </div>
                        <div>{user.displayName}</div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={user.role === 'admin' ? styles.adminBadge : styles.userBadge}>
                        {user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                      </span>
                    </td>
                    <td>
                      <span className={user.isPremium ? styles.premiumBadge : styles.regularBadge}>
                        {user.isPremium ? 'Premium' : 'Normal'}
                      </span>
                    </td>
                    <td>
                      {user.suspended ? (
                        <span className={styles.suspendedBadge}>Askıya Alınmış</span>
                      ) : (
                        <span className={styles.regularBadge}>Aktif</span>
                      )}
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString('tr-TR')}</td>
                    <td>
                      <div className={styles.actionButtons}>
                        {user.role !== 'admin' && (
                          <button 
                            className={styles.adminButton}
                            onClick={() => onMakeAdmin(user.uid)}
                          >
                            Yönetici Yap
                          </button>
                        )}
                        
                        {!user.isPremium ? (
                          <button 
                            className={styles.premiumButton}
                            onClick={() => onMakePremium(user.uid)}
                          >
                            Premium Yap
                          </button>
                        ) : (
                          <button 
                            className={styles.premiumButton}
                            onClick={() => onRemovePremium(user.uid)}
                          >
                            Premium Kaldır
                          </button>
                        )}
                        
                        {!user.suspended ? (
                          <button 
                            className={styles.suspendButton}
                            onClick={() => openSuspendModal(user)}
                          >
                            Askıya Al
                          </button>
                        ) : (
                          <button 
                            className={styles.unsuspendButton}
                            onClick={() => onUnsuspendUser(user.uid)}
                          >
                            Askıyı Kaldır
                          </button>
                        )}
                        
                        <button 
                          className={styles.deleteButton}
                          onClick={() => onDeleteUser(user.uid)}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? 'Arama sonucunda kullanıcı bulunamadı.' : 'Kullanıcı bulunamadı.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {pagination.hasMore && (
          <div className={styles.pagination}>
            <button 
              className={styles.paginationButton}
              onClick={loadNextPage}
            >
              Daha Fazla Göster
            </button>
          </div>
        )}
      </div>
      
      {showSuspendModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Kullanıcıyı Askıya Al</h3>
              <button 
                className={styles.modalCloseButton}
                onClick={() => setShowSuspendModal(false)}
              >
                <MdClose />
              </button>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Kullanıcı:</label>
              <div>{selectedUser.displayName} ({selectedUser.email})</div>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Askıya Alma Nedeni:</label>
              <textarea 
                className={styles.formTextarea}
                value={suspensionReason}
                onChange={(e) => setSuspensionReason(e.target.value)}
                placeholder="Kullanıcının neden askıya alındığını açıklayın..."
              />
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCancelButton}
                onClick={() => setShowSuspendModal(false)}
              >
                İptal
              </button>
              <button 
                className={styles.modalSubmitButton}
                onClick={handleSuspendUser}
                disabled={!suspensionReason.trim()}
              >
                Askıya Al
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
